/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx, css } from '@emotion/react';
import React, { useState, useEffect, useMemo } from 'react';
import DetailsTable from "../../../Table/DetailsTable";
import { 
    DetailsList, 
    DetailsListLayoutMode, 
    SelectionMode, 
    SearchBox,
    DatePicker,
    PrimaryButton,
    Spinner,
    MessageBar,
    MessageBarType,
    Dropdown
} from '@fluentui/react';
import useAxios from 'axios-hooks';
import { json2csv } from 'json-2-csv';

const container = css`
    padding: 20px;
`;

const filtersContainer = css`
    display: flex;
    gap: 10px;
    margin-bottom: 20px;
    align-items: flex-end;
    justify-content: flex-start;
    flex-wrap: wrap;
`;

const datePickerContainer = css`
    display: flex;
    flex-direction: column;
    width: 150px;
    .ms-TextField-field {
        height: 100%;
        display: flex;
        align-items: center;
    }
`;

const searchContainer = css`
    width: 250px;
    display: flex;
    flex-direction: column;
    padding-bottom:5px;
    .ms-SearchBox{
        width: 100%;
        height: 36px;
    }
`;

const exportButtonContainer = css`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
`;

const exportButton = css`
    margin-bottom: 8px;
`;

const dropdownContainer = css`
    display: flex;
    flex-direction: column;
    width: 200px;
    margin-bottom: 5px;
`;

const SsoVsManualAudit = () => {
    const [searchText, setSearchText] = useState('');
    const [selectedCoBrand, setSelectedCoBrand] = useState(null);
    
    const [fromDate, setFromDate] = useState(() => {
        const date = new Date();
        date.setDate(date.getDate() - 30);
        return date;
    });
    
    const [toDate, setToDate] = useState(new Date());

    const formatDateForAPI = (date) => {
        if (!date) return '';
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const day = date.getDate().toString().padStart(2, '0');
        const year = date.getFullYear();
        return `${month}-${day}-${year}`;
    };

    const apiUrl = useMemo(() => {
        const params = new URLSearchParams();
        if (fromDate) params.append('fromDate', formatDateForAPI(fromDate));
        if (toDate) params.append('toDate', formatDateForAPI(toDate));
        return `internal/cobrand/sso-vs-manual?${params.toString()}`;
    }, [fromDate, toDate]);

    const [{ data: auditData, loading, error }, refetch] = useAxios(apiUrl);

    useEffect(() => {
        refetch();
    }, [fromDate, toDate, refetch]);

    // Get distinct cobrands for dropdown
    const cobrandOptions = useMemo(() => {
        if (!auditData) return [];
        
        const uniqueCobrands = auditData.reduce((acc, item) => {
            if (item.coBrandId && item.name) {
                const key = item.coBrandId;
                if (!acc.some(option => option.key === key)) {
                    acc.push({
                        key: item.coBrandId,
                        text: item.name
                    });
                }
            }
            return acc;
        }, []);
        
        return uniqueCobrands.sort((a, b) => a.text.localeCompare(b.text));
    }, [auditData]);

    const filteredData = useMemo(() => {
        if (!auditData) return [];
        
        let filtered = auditData;
        
        // Filter by selected cobrand
        if (selectedCoBrand) {
            filtered = filtered.filter(item => item.coBrandId === selectedCoBrand);
        }
        
        // Filter by search text
        if (searchText) {
            const lowerSearchText = searchText.toLowerCase();
            filtered = filtered.filter(item => 
                item.name?.toLowerCase().includes(lowerSearchText) ||
                item.username?.toLowerCase().includes(lowerSearchText) ||
                item.accessType?.toLowerCase().includes(lowerSearchText) ||
                item.registrationMethod?.toLowerCase().includes(lowerSearchText)
            );
        }
        
        return filtered;
    }, [auditData, searchText, selectedCoBrand]);

    // Export filtered data as CSV
    const exportToCSV = async () => {
        if (!filteredData || filteredData.length === 0) {
            return;
        }

        try {
            // Prepare data with formatted dates and proper field names
            const dataForExport = filteredData.map(item => ({
                'CoBrand ID': item.coBrandId || '',
                'Name': item.name || '',
                'Access Type': item.accessType || '',
                'Username': item.username || '',
                'Member ID': item.memberId || '',
                'Registration Method': item.registrationMethod || '',
                'Created On': item.createdOn ? new Date(item.createdOn).toLocaleDateString() : ''
            }));

            // Convert to CSV using json-2-csv
            const csvContent = await json2csv(dataForExport);

            // Create and download the file
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            const url = URL.createObjectURL(blob);
            link.setAttribute('href', url);
            link.setAttribute('download', `sso-vs-manual-audit-${new Date().toISOString().split('T')[0]}.csv`);
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (error) {
            console.error('Error exporting CSV:', error);
        }
    };

    const columns = [
        {
            key: 'coBrandId',
            name: 'CoBrand ID',
            fieldName: 'coBrandId',
            minWidth: 100,
            maxWidth: 120,
            isResizable: true,
        },
        {
            key: 'name',
            name: 'Name',
            fieldName: 'name',
            minWidth: 120,
            maxWidth: 150,
            isResizable: true,
        },
        {
            key: 'accessType',
            name: 'Access Type',
            fieldName: 'accessType',
            minWidth: 120,
            maxWidth: 150,
            isResizable: true,
        },
        {
            key: 'username',
            name: 'Username',
            fieldName: 'username',
            minWidth: 150,
            maxWidth: 200,
            isResizable: true,
        },
        {
            key: 'firstName',
            name: 'FirstName',
            fieldName: 'firstName',
            minWidth: 150,
            maxWidth: 200,
            isResizable: true,
        },
        {
            key: 'lastName',
            name: 'LastName',
            fieldName: 'lastName',
            minWidth: 150,
            maxWidth: 200,
            isResizable: true,
        },
        {
            key: 'studentID',
            name: 'StudentID',
            fieldName: 'studentID',
            minWidth: 150,
            maxWidth: 200,
            isResizable: true,
        },
        {
            key: 'clientAlternateUserId',
            name: 'ClientAlternateUserId',
            fieldName: 'clientAlternateUserId',
            minWidth: 150,
            maxWidth: 200,
            isResizable: true,
        },
        {
            key: 'memberId',
            name: 'Member ID',
            fieldName: 'memberId',
            minWidth: 100,
            maxWidth: 120,
            isResizable: true,
        },
        {
            key: 'registrationMethod',
            name: 'Registration Method',
            fieldName: 'registrationMethod',
            minWidth: 150,
            maxWidth: 200,
            isResizable: true,
        },
        {
            key: 'createdOn',
            name: 'Created On',
            fieldName: 'createdOn',
            minWidth: 120,
            maxWidth: 150,
            isResizable: true,
            onRender: (item) => {
                if (!item.createdOn) return '';
                const date = new Date(item.createdOn);
                return date.toLocaleDateString();
            },
        },
    ];

    if (loading) {
        return (
            <div css={container}>
                <Spinner label="Loading audit data..." />
            </div>
        );
    }

    return (
        <div css={container}>
            <h3>Cobrands with SSO only but have manual signups</h3>
            
            <div css={filtersContainer}>
                <div css={datePickerContainer}>
                    <DatePicker
                        label="From Date"
                        value={fromDate}
                        onSelectDate={setFromDate}
                        placeholder="Select start date"
                    />
                </div>
                
                <div css={datePickerContainer}>
                    <DatePicker
                        label="To Date"
                        value={toDate}
                        onSelectDate={setToDate}
                        placeholder="Select end date"
                    />
                </div>
                
                <div css={dropdownContainer}>
                    <Dropdown
                        label="CoBrand Filter"
                        placeholder="Filter by CoBrand"
                        options={cobrandOptions}
                        selectedKey={selectedCoBrand}
                        onChange={(_, option) => setSelectedCoBrand(option ? option.key : null)}
                    />
                </div>
                
                <div css={searchContainer}>
                    <SearchBox
                        label="Search"
                        placeholder="Search"
                        value={searchText}
                        onChange={(_, newValue) => setSearchText(newValue || '')}
                    />
                </div>
                
                <div css={exportButtonContainer}>
                    <PrimaryButton
                        text="Export CSV"
                        onClick={exportToCSV}
                        disabled={!filteredData || filteredData.length === 0}
                        css={exportButton}
                    />
                </div>
                
                
            </div>

            {error && (
                <MessageBar messageBarType={MessageBarType.error} style={{ marginBottom: '20px' }}>
                    Error loading audit data: {error.message}
                </MessageBar>
            )}

            {filteredData && (
                <>
                    <p>Found {filteredData.length} record{filteredData.length !== 1 ? 's' : ''}</p>
                    <DetailsTable
                        items={filteredData}
                        columns={columns}
                        layoutMode={DetailsListLayoutMode.justified}
                        selectionMode={SelectionMode.none}
                        isHeaderVisible={true}
                    />
                </>
            )}
        </div>
    );
};

export default SsoVsManualAudit;
